<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';

const props = defineProps({
  avatars: { type: Array, default: () => [] },
  fallbackAvatarUrl: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
});
const images = defineModel({ type: Array, default: undefined });
const { t } = useI18n();
const fileInput = ref(null);
const previewImages = ref([]);
const replacementIndex = ref(null);
const error = ref('');
const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const maxFileSize = 15 * 1024 * 1024;
const maxImages = 4;
const currentImages = computed(() => images.value ?? props.avatars);

const releasePreviews = () => {
  previewImages.value.forEach(image => {
    if (image.file) URL.revokeObjectURL(image.url);
  });
};
watch(
  currentImages,
  value => {
    releasePreviews();
    previewImages.value = value.map(image => ({
      ...image,
      url: image.file ? URL.createObjectURL(image.file) : image.url,
    }));
  },
  { immediate: true }
);
onBeforeUnmount(releasePreviews);

const chooseImages = index => {
  replacementIndex.value = index;
  fileInput.value.multiple = index === null;
  fileInput.value.click();
};

const upload = event => {
  const selected = [...event.target.files];
  event.target.value = '';
  if (!selected.length) return;
  const retained = currentImages.value.filter(
    (_, index) => index !== replacementIndex.value
  );
  if (retained.length + selected.length > maxImages) {
    error.value = t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.ONLINE_AVATAR.COUNT_LIMIT');
    return;
  }
  if (
    selected.some(
      file =>
        !allowedTypes.includes(file.type) ||
        !file.size ||
        file.size > maxFileSize
    )
  ) {
    error.value = t(
      'INBOX_MGMT.ADD.WEBSITE_CHANNEL.ONLINE_AVATAR.INVALID_FILE'
    );
    return;
  }
  error.value = '';
  images.value = [...retained, ...selected.map(file => ({ file }))];
  replacementIndex.value = null;
};

const remove = index => {
  error.value = '';
  images.value = currentImages.value.filter(
    (_, itemIndex) => itemIndex !== index
  );
};
</script>

<template>
  <SettingsFieldSection
    :label="t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.ONLINE_AVATAR.LABEL')"
    :help-text="t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.ONLINE_AVATAR.HELP_TEXT')"
  >
    <div class="flex flex-col gap-3">
      <div class="flex flex-wrap items-start gap-4">
        <div
          v-for="(image, index) in previewImages"
          :key="image.id || image.url"
          class="flex flex-col items-center gap-2"
        >
          <Avatar
            :src="image.url"
            :size="64"
            name=""
            icon-name="i-lucide-headset"
            rounded-full
          />
          <div class="flex gap-1">
            <Button
              type="button"
              size="xs"
              color="slate"
              variant="ghost"
              icon="i-lucide-refresh-cw"
              :disabled="disabled"
              :aria-label="
                t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.ONLINE_AVATAR.REPLACE', {
                  index: index + 1,
                })
              "
              :title="
                t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.ONLINE_AVATAR.REPLACE', {
                  index: index + 1,
                })
              "
              @click="chooseImages(index)"
            />
            <Button
              type="button"
              size="xs"
              color="ruby"
              variant="ghost"
              icon="i-lucide-trash-2"
              :disabled="disabled"
              :aria-label="
                t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.ONLINE_AVATAR.REMOVE', {
                  index: index + 1,
                })
              "
              :title="
                t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.ONLINE_AVATAR.REMOVE', {
                  index: index + 1,
                })
              "
              @click="remove(index)"
            />
          </div>
        </div>
        <Avatar
          v-if="!previewImages.length"
          :src="fallbackAvatarUrl"
          :size="64"
          name=""
          icon-name="i-lucide-headset"
          rounded-full
        />
      </div>
      <div>
        <Button
          type="button"
          size="sm"
          color="slate"
          variant="outline"
          icon="i-lucide-upload"
          :disabled="disabled || currentImages.length >= maxImages"
          :label="t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.ONLINE_AVATAR.UPLOAD')"
          @click="chooseImages(null)"
        />
      </div>
      <p class="m-0 text-label-small text-n-slate-11">
        {{ t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.ONLINE_AVATAR.FORMATS') }}
      </p>
      <input
        ref="fileInput"
        type="file"
        multiple
        accept="image/jpeg,image/png,image/gif,image/webp"
        class="hidden"
        :disabled="disabled"
        :aria-label="t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.ONLINE_AVATAR.UPLOAD')"
        @change="upload"
      />
    </div>
    <p v-if="error" role="alert" class="mt-2 text-label-small text-n-ruby-11">
      {{ error }}
    </p>
  </SettingsFieldSection>
</template>
